import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';

/**
 * A page at a time for a section of a page (owner rulings 2026-10-29: on
 * a page that reviews information no single section takes more than ~80%
 * of the screen — the next section's heading always shows, so people know
 * there is more). The page size comes from the window's own height: as
 * many cards as fit in 80% of it after the heading and the bar (QA
 * measured a task card at ~92 px wide-screen, ~184 px on a phone), 3 to
 * 10. A 1080p laptop at 100% scaling (~937 px of browser) gets 7 — the
 * owner designs for 1080p (2026-10-29); a taller monitor gets up to 10,
 * a phone 3, and 125% Windows scaling 5.
 * `usePager` slices; `PagerBar` reads "6–10 of 42 · ‹ Previous · Next ›"
 * (nothing when one page holds it all) and, after a turn, brings the top of
 * the list (`anchor`) into view and gives it focus. The page goes back to 1
 * when `resetKey` changes (another store, the other task tab, a filter).
 * Standard pages aren't made for paper (owner ruling: what people print is
 * a report) — printing shows the page on screen.
 */
/** The page that holds the row a `page` of `from` rows started with, at `to` rows a page (pure; exported for the test). */
export function pageAfterResize(page: number, from: number, to: number): number {
  return Math.floor((page * from) / Math.max(1, to));
}

/** Used when the window can't be measured. */
export const PAGE_SIZE = 7;
const MIN_PAGE = 3;
const MAX_PAGE = 10;
/** Task card heights QA measured (the tallest card a pager holds). */
const CARD_PX = { wide: 92, phone: 184 };
/** The section heading + the pager bar. */
const CHROME_PX = 90;

/** How many cards fit in 80% of a window this tall (exported for the test); `rowPx` = this list's own row height. */
export function pageSizeFor(windowHeight: number, phone: boolean, rowPx?: number): number {
  const n = Math.floor((windowHeight * 0.8 - CHROME_PX) / (rowPx ?? (phone ? CARD_PX.phone : CARD_PX.wide)));
  return Math.max(MIN_PAGE, Math.min(MAX_PAGE, n));
}

/** What fits this window, but never more than `max` (store page tasks: 5 — taller cards, owner test 2026-10-29). */
export function fitPageSize(max: number): number {
  return Math.min(max, defaultSize());
}

/**
 * A page size for a list whose rows aren't task cards (UI-6 review): on a
 * phone a fixed count of its cards, elsewhere as many `widePx`-tall rows as
 * fit 80% of the window (3–10).
 */
export function fitRows(widePx: number, phoneCount: number): number {
  try {
    if (isPhoneWidth()) return phoneCount;
    if (typeof window === 'undefined' || !window.innerHeight) return PAGE_SIZE;
    return pageSizeFor(window.innerHeight, false, widePx);
  } catch {
    return PAGE_SIZE;
  }
}

/** A phone-width window (≤ 720 px — the app's phone layout). */
export function isPhoneWidth(): boolean {
  try {
    return typeof window !== 'undefined' && (window.matchMedia?.('(max-width: 720px)').matches ?? false);
  } catch {
    return false;
  }
}

/** Rows `rowPx` tall that fit 80% of a `windowHeight` window after `chromePx`, `min`–`max` (pure; exported for the test). */
export function sizeFromRows(windowHeight: number, chromePx: number, rowPx: number, min = MIN_PAGE, max = MAX_PAGE): number {
  const fit = Math.floor((windowHeight * 0.8 - chromePx) / rowPx);
  return Math.max(min, Math.min(max, fit));
}

/**
 * A page size from the rows actually drawn (UI review 3, E3 / D2 / D3 / D4:
 * a fixed row height undercounted two-line board rows and tall task cards,
 * so sections reached 86–217% of the window). After each draw it measures
 * the rows inside `listRef` (`rowSelector`, default its children) — from the
 * first row's top to the last row's bottom plus its margin, so card gaps
 * count — and the list's table head. It keeps the TALLEST row and head seen,
 * so the size can only settle downwards: no back-and-forth between two sizes
 * (QA: a head that re-wraps could otherwise loop) and pages don't reshuffle
 * as you turn them; a window resize starts again. It returns as many rows as
 * fit 80% of the window after `chromePx` (the section's heading, filters and
 * pager bar), `min`–`max`. On a phone it returns `phone` (cards are measured
 * by eye there). Before anything is measured (first draw, tests) it returns
 * `initial`.
 */
export function useFitPageSize(
  listRef: RefObject<HTMLElement | null>,
  opts: { initial: number; chromePx: number; phone: number; rowSelector?: string; min?: number; max?: number },
): number {
  const { initial, chromePx, phone, rowSelector = ':scope > *', min = MIN_PAGE, max = MAX_PAGE } = opts;
  const [size, setSize] = useState(initial);
  // A resize bumps this so the list is measured again even when the size doesn't change (QA).
  const [, setResizes] = useState(0);
  const tallest = useRef({ row: 0, head: 0 });
  const lastWidth = useRef(typeof window === 'undefined' ? 0 : window.innerWidth);
  useEffect(() => {
    // Measure again from scratch; the size then moves straight from the old value to the new one
    // (QA: resetting it to `initial` first re-mapped the page twice and drifted the reader back).
    // Phones fire height-only resizes as the toolbar or keyboard comes and goes — ignored.
    const onResize = () => {
      const width = window.innerWidth;
      if (width === lastWidth.current && isPhoneWidth()) return;
      lastWidth.current = width;
      tallest.current = { row: 0, head: 0 };
      setResizes((n) => n + 1);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  useLayoutEffect(() => {
    try {
      if (isPhoneWidth()) {
        if (size !== phone) setSize(phone);
        return;
      }
      const el = listRef.current;
      if (!el || !window.innerHeight) return;
      const rows = el.querySelectorAll<HTMLElement>(rowSelector);
      const firstEl = rows[0];
      const lastEl = rows[rows.length - 1];
      if (!firstEl || !lastEl) return;
      const first = firstEl.getBoundingClientRect();
      const last = lastEl.getBoundingClientRect();
      const gap = parseFloat(getComputedStyle(lastEl).marginBottom) || 0;
      const avg = (last.bottom - first.top + gap) / rows.length;
      if (!(avg > 0)) return; // not laid out (tests, a closed <details>)
      const head = (el.closest('table') ?? el).querySelector('thead')?.getBoundingClientRect().height ?? 0;
      const seen = tallest.current;
      const firstMeasure = seen.row === 0;
      seen.row = Math.max(seen.row, avg);
      seen.head = Math.max(seen.head, head);
      const next = sizeFromRows(window.innerHeight, chromePx + seen.head, seen.row, min, max);
      // The first measure may grow or shrink the guess; after that only shrink (both tallests only grow).
      if (firstMeasure ? next !== size : next < size) setSize(next);
    } catch {
      /* keep the size we have */
    }
  });
  return size;
}

function defaultSize(): number {
  try {
    if (typeof window === 'undefined' || !window.innerHeight) return PAGE_SIZE;
    return pageSizeFor(window.innerHeight, window.matchMedia?.('(max-width: 720px)').matches ?? false);
  } catch {
    return PAGE_SIZE;
  }
}

export interface Pager<T> {
  items: T[];
  page: number;
  pages: number;
  total: number;
  first: number;
  last: number;
  prev(): void;
  next(): void;
  /** "Show all" (boards and long lists, owner ruling 2026-10-29): every item on one page until `showFewer`. */
  showingAll: boolean;
  showAll(): void;
  showFewer(): void;
  /** Pages there would be without "Show all". */
  pagesWhenPaged: number;
  /** Put on the element just above the list (a heading or `<PagerTop>`): scrolled to and focused after a page turn. */
  anchor: { current: HTMLElement | null };
}

/** `size` omitted = what fits this window (see `pageSizeFor`). */
export function usePager<T>(all: readonly T[] | null, resetKey: unknown, sizeAsked?: number): Pager<T> {
  // A size the caller gives may change (useFitPageSize settles after a draw); the window's own is fixed.
  const [fallback] = useState(defaultSize);
  const size = Math.max(1, sizeAsked ?? fallback);
  const [page, setPage] = useState(0);
  // A size that changes (useFitPageSize settling) keeps the first row shown on screen, not the page number (QA: row 5 repeated).
  const [shownSize, setShownSize] = useState(size);
  if (shownSize !== size) {
    setShownSize(size);
    setPage(pageAfterResize(page, shownSize, size));
  }
  const [showingAll, setShowingAll] = useState(false);
  const anchor = useRef<HTMLElement | null>(null);
  useEffect(() => setPage(0), [resetKey]);
  const total = all?.length ?? 0;
  const pagesWhenPaged = Math.max(1, Math.ceil(total / size));
  const pages = showingAll ? 1 : pagesWhenPaged;
  const current = Math.min(page, pages - 1);
  const start = showingAll ? 0 : current * size;
  const end = showingAll ? total : start + size;
  return {
    items: (all ?? []).slice(start, end),
    page: current,
    pages,
    total,
    first: total === 0 ? 0 : start + 1,
    last: Math.min(total, end),
    prev: () => setPage(Math.max(0, current - 1)),
    next: () => setPage(Math.min(pages - 1, current + 1)),
    showingAll,
    showAll: () => setShowingAll(true),
    showFewer: () => {
      setShowingAll(false);
      setPage(0);
    },
    pagesWhenPaged,
    anchor,
  };
}

/** An invisible focus / scroll target just above a list that has no heading of its own. */
export function PagerTop<T>({ pager }: { pager: Pager<T> }) {
  return (
    <span
      ref={(el) => {
        pager.anchor.current = el;
      }}
      tabIndex={-1}
      className="pager-top"
    />
  );
}

export function PagerBar<T>({ pager, noun, allowAll = false }: { pager: Pager<T>; noun: string; allowAll?: boolean }) {
  if (pager.pagesWhenPaged <= 1) return null;
  if (pager.showingAll) {
    return (
      <p className="pager-bar">
        <span className="muted">
          All {pager.total} {noun}
        </span>
        <button type="button" className="btn btn-small" onClick={() => {
          pager.showFewer();
          requestAnimationFrame(() => {
            const el = pager.anchor.current;
            if (!el) return;
            if (el.getBoundingClientRect().top < 0) el.scrollIntoView({ block: 'start' });
            el.focus({ preventScroll: true });
          });
        }}>
          Show fewer
        </button>
      </p>
    );
  }
  // After the new page draws: its top in view (only when it's above the screen) and focus there, never on a now-disabled button.
  const turn = (go: () => void) => {
    go();
    requestAnimationFrame(() => {
      const el = pager.anchor.current;
      if (!el) return;
      if (el.getBoundingClientRect().top < 0) el.scrollIntoView({ block: 'start' });
      el.focus({ preventScroll: true });
    });
  };
  return (
    <p className="pager-bar">
      <span className="muted">
        {pager.first}–{pager.last} of {pager.total} {noun}
      </span>
      <button type="button" className="btn btn-small" onClick={() => turn(pager.prev)} disabled={pager.page === 0}>
        ‹ Previous
      </button>
      <button type="button" className="btn btn-small" onClick={() => turn(pager.next)} disabled={pager.page >= pager.pages - 1}>
        Next ›
      </button>
      {allowAll && (
        <button
          type="button"
          className="btn btn-small"
          onClick={() => {
            pager.showAll();
            // The button goes away: keep keyboard focus on the list, not <body> (QA).
            requestAnimationFrame(() => pager.anchor.current?.focus({ preventScroll: true }));
          }}
        >
          Show all {pager.total}
        </button>
      )}
    </p>
  );
}
